import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { site } from "@/config/site";
import { papers, publications } from "@/lib/content/load";
import type { PaperFrontmatter } from "@/content/schema";
import { track } from "@/lib/analytics";
import { headingText } from "@/lib/heading";

const researchThemes: {
  id: PaperFrontmatter["researchTheme"];
  label: string;
  blurb: string;
}[] = [
  {
    id: "computational-social-science",
    label: "Computational social science and behavioral algorithms",
    blurb:
      "The 2023 Singapore conference paper on human behavioral algorithms and sentimental equilibrium.",
  },
  {
    id: "product-line-architecture",
    label: "Product-line architecture and strategic reuse",
    blurb: "Commonality in a core, variability at the edges — the ancestor of EcosystemCode.",
  },
  {
    id: "application-assembly",
    label: "Application assembly and service-based architecture",
    blurb: "Certifiable assets, web services, predictable assembly.",
  },
  {
    id: "innovation-networks",
    label: "Innovation systems, product quality, and network analysis",
    blurb: "Ecosystems, portfolios, and relationship as evidence.",
  },
];

export function PublicationsPage() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = window.decodeURIComponent(hash.slice(1));
    const scroll = () => document.getElementById(id)?.scrollIntoView({ block: "start" });
    scroll();
    const timer = window.setTimeout(scroll, 0);
    return () => window.clearTimeout(timer);
  }, [hash]);

  const books = publications.filter((item) => item.meta.type === "book");
  const patents = publications.filter((item) => item.meta.type === "patent");
  const resources = publications.filter((item) => item.meta.type === "resource");

  return (
    <>
      <Seo
        title="Resources"
        description="Books, patents, and academic papers — product lines, assembly, networks, and behavioral algorithms."
        path="/resources"
      />
      <section className="wrap section" style={{ paddingTop: "4.5rem" }}>
        <p className="eyebrow">Resources</p>
        <h1>Books and patents</h1>
        <p className="lede">
          Books and patents first. The academic papers follow. Full records are on{" "}
          <a href={site.profiles.academia}>Academia.edu</a> and{" "}
          <a href={site.profiles.researchgate}>ResearchGate</a>.
        </p>
      </section>
      <section className="wrap grid-2">
        {books.map((item) => (
          <article className="card" key={item.meta.slug}>
            <p className="badge badge-human">Book</p>
            <h2>
              <Link to={item.meta.canonicalPath}>{headingText(item.meta.title)}</Link>
            </h2>
            <p>{item.meta.summary}</p>
            {item.meta.slug === "built-to-thrive" ? (
              <p>
                <a
                  href="https://chatgpt.com/g/g-ZS1LEEzIv-built-to-thrive-in-the-world-of-ai"
                  onClick={() => track("book-gpt-clicked")}
                >
                  Built to Thrive GPT
                </a>
              </p>
            ) : null}
          </article>
        ))}
      </section>
      <section className="wrap section" id="patents">
        <h2>Patents</h2>
        <div className="grid-2">
          {patents.map((item) => (
            <article className="card" key={item.meta.slug}>
              <h3>
                <Link to={item.meta.canonicalPath}>{headingText(item.meta.title)}</Link>
              </h3>
              <p>{item.meta.summary}</p>
              {item.meta.documentHref ? (
                <p>
                  <a href={item.meta.documentHref} download>
                    Download patent document (PDF)
                  </a>
                  {item.meta.patentNumber ? (
                    <>
                      {" · "}
                      <span className="meta">{item.meta.patentNumber}</span>
                    </>
                  ) : null}
                </p>
              ) : null}
            </article>
          ))}
        </div>
      </section>
      <section className="wrap section" id="research">
        <h2>Research</h2>
        {researchThemes.map((theme) => {
          const group = papers.filter((item) => item.meta.researchTheme === theme.id);
          if (!group.length) return null;
          return (
            <div className="section" key={theme.id}>
              <p className="section-kicker">{theme.label}</p>
              <p>{theme.blurb}</p>
              <div className="stack">
                {group.map((item) => (
                  <article className="card" key={item.meta.slug}>
                    <p className="meta">
                      {item.meta.year} · {item.meta.publicationType}
                      {item.meta.venue ? ` · ${item.meta.venue}` : ""}
                    </p>
                    <h3>
                      <Link to={item.meta.canonicalPath}>{headingText(item.meta.title)}</Link>
                    </h3>
                    <p>{item.meta.summary}</p>
                  </article>
                ))}
              </div>
            </div>
          );
        })}
      </section>
      <section className="wrap section">
        <h2>Resource library</h2>
        {resources.map((item) => (
          <article className="card" key={item.meta.slug}>
            <h3>
              <Link to={item.meta.canonicalPath}>{headingText(item.meta.title)}</Link>
            </h3>
            <p>{item.meta.summary}</p>
          </article>
        ))}
      </section>
    </>
  );
}
